import * as yup from 'yup'
import { IntlShape, defineMessages } from 'react-intl'
import { isEmpty } from 'lodash'
import { INewUserValues } from 'features/Users/types'

const companyMessages = defineMessages({
  required: {
    id: 'users.form.company.required',
    defaultMessage: 'The company is required'
  }
})

const firstNameMessages = defineMessages({
  required: {
    id: 'users.form.firstName.required',
    defaultMessage: 'The first name is required'
  },
  max: {
    id: 'users.form.firstName.max',
    defaultMessage: 'The first name can be up to 255 characters'
  }
})

const lastNameMessages = defineMessages({
  required: {
    id: 'users.form.lastName.required',
    defaultMessage: 'The last name is required'
  },
  max: {
    id: 'users.form.lastName.max',
    defaultMessage: 'The last name can be up to 255 characters'
  }
})

const usernameMessages = defineMessages({
  required: {
    id: 'users.form.username.required',
    defaultMessage: 'The username is required'
  },
  max: {
    id: 'users.form.username.max',
    defaultMessage: 'The username can be up to 255 characters'
  }
})

const emailMessages = defineMessages({
  max: {
    id: 'users.form.email.max',
    defaultMessage: 'The email can be up to 255 characters'
  },
  format: {
    id: 'users.form.email.format',
    defaultMessage: 'The format is incorrect'
  },
  required: {
    id: 'users.form.email.required',
    defaultMessage: 'The email is required'
  }
})

const passwordMessages = defineMessages({
  required: {
    id: 'users.form.password.required',
    defaultMessage: 'The password is required'
  },
  max: {
    id: 'users.form.password.max',
    defaultMessage: 'The password can be up to 255 characters'
  },
  min: {
    id: 'users.form.password.min',
    defaultMessage: 'The password should have at least 8 characters'
  },
  capitalLetter: {
    id: 'users.form.password.capitalLetter',
    defaultMessage: 'The password should have at least 1 capital letter'
  },
  smallLetter: {
    id: 'users.form.password.smallLetter',
    defaultMessage: 'The password should have at least 1 small letter'
  },
  digit: {
    id: 'users.form.password.digit',
    defaultMessage: 'The password should have at least 1 digit'
  }
})

const positionMessages = defineMessages({
  required: {
    id: 'users.form.position.required',
    defaultMessage: 'The position is required'
  },
  max: {
    id: 'users.form.position.max',
    defaultMessage: 'The position can be up to 255 characters'
  }
})

const testRequired = (value: TSFixMe, isEdit: TSFixMe): TSFixMe => {
  if (!isEdit) {
    return !isEmpty(value)
  }

  return true
}

export const schema = (intl: IntlShape, isEdit: boolean): yup.SchemaOf<INewUserValues> => {
  return yup.object().shape({
    verticals: yup.array().of(yup.string().required()),
    companies: (yup.array() as yup.SchemaOf<INewUserValues['companies']>).min(
      1,
      intl.formatMessage(companyMessages.required)
    ),
    firstName: yup
      .string()
      .required(intl.formatMessage(firstNameMessages.required))
      .max(255, intl.formatMessage(firstNameMessages.max)),
    username: yup
      .string()
      .required(intl.formatMessage(usernameMessages.required))
      .max(255, intl.formatMessage(usernameMessages.max)),
    lastName: yup
      .string()
      .required(intl.formatMessage(lastNameMessages.required))
      .max(255, intl.formatMessage(lastNameMessages.max)),
    email: yup
      .string()
      .email(intl.formatMessage(emailMessages.format))
      .max(255, intl.formatMessage(emailMessages.max))
      .when('mfaEnabled', {
        is: true,
        then: yup.string().required(intl.formatMessage(emailMessages.required))
      }),
    password: yup
      .string()
      .test('is-password-required', intl.formatMessage(passwordMessages.required), function (value) {
        return testRequired(value, isEdit)
      })
      .max(255, intl.formatMessage(passwordMessages.max))
      .min(8, intl.formatMessage(passwordMessages.min))
      .matches(/[A-Z]/, intl.formatMessage(passwordMessages.capitalLetter))
      .matches(/[a-z]/, intl.formatMessage(passwordMessages.smallLetter))
      .matches(/[0-9]/, intl.formatMessage(passwordMessages.digit)),
    position: yup
      .string()
      .required(intl.formatMessage(positionMessages.required))
      .max(255, intl.formatMessage(positionMessages.max)),
    permissionIDs: yup.array().of(yup.string().required()),
    roles: yup.array(),
    mfaEnabled: yup.boolean().required(),
    passwordExpirationEnabled: yup.boolean().required()
  })
}
